import { PortalData, PortalAnalysisData, ViewType } from './PortalAnalysisChart.types'
import {
  formatPrice as formatCentralizedPrice,
  formatPricePerSqm as formatCentralizedPricePerSqm,
  formatNumber as formatCentralizedNumber,
  formatPercentage as formatCentralizedPercentage,
} from '../../../utils/number/formatUtils'

export const formatNumber = (num: number): string => {
  return formatCentralizedNumber(num)
}

export const formatPrice = (price: number, isRental: boolean = false): string => {
  return formatCentralizedPrice(price, isRental)
}

export const formatPricePerSqm = (price: number): string => {
  return formatCentralizedPricePerSqm(price)
}

export const formatPercentage = (value: number): string => {
  return formatCentralizedPercentage(value)
}

export const processPortalData = (aggregations: any): PortalAnalysisData => {
  // Check for the correct nested structure
  const entitiesAgg = aggregations?.entities
  if (!entitiesAgg?.entity_names?.buckets) {
    return {
      portals: [],
      totalListings: 0,
      totalPortals: 0,
      marketLeader: null,
      top5Share: 0,
    }
  }

  const totalListings = aggregations.total_listings?.value || 0
  const allPortals = entitiesAgg.entity_names.buckets

  // Transform portals (no minimum listing filter for portals)
  const processedPortals = allPortals
    .map((portal: any): PortalData => {
      const listingCount = portal.back_to_listing.doc_count
      const avgPrice = portal.back_to_listing.avg_price?.value || 0
      const avgPricePerSqm = portal.back_to_listing.avg_price_per_sqm?.value || 0
      const totalValue = portal.back_to_listing.total_value?.value || 0
      const regions = portal.back_to_listing.regions?.buckets.map((r: any) => r.key) || []
      const propertyTypes =
        portal.back_to_listing.property_types?.buckets.map((pt: any) => ({
          type: pt.key,
          count: pt.doc_count,
        })) || []

      return {
        name: portal.key || 'Unknown',
        listingCount,
        avgPrice,
        avgPricePerSqm,
        totalValue,
        regionCount: regions.length,
        marketShare: (listingCount / totalListings) * 100,
        regions,
        propertyTypes,
      }
    })
    .sort((a: PortalData, b: PortalData) => b.listingCount - a.listingCount)

  // Calculate top 5 market share
  const top5 = processedPortals.slice(0, 5)
  const top5Total = top5.reduce((sum: number, p: PortalData) => sum + p.listingCount, 0)
  const top5Share = totalListings > 0 ? (top5Total / totalListings) * 100 : 0

  return {
    portals: processedPortals,
    totalListings,
    totalPortals: processedPortals.length,
    marketLeader: processedPortals[0] || null,
    top5Share,
  }
}

export const categorizePortals = (
  portals: PortalData[],
  viewType: ViewType = 'listings',
  t: TranslationFn = defaultT
) => {
  const large = t('portalAnalysis.treemap.largePortals')
  const medium = t('portalAnalysis.treemap.mediumPortals')
  const small = t('portalAnalysis.treemap.smallPortals')

  const groups: Record<string, PortalData[]> = {
    [large]: [],
    [medium]: [],
    [small]: [],
  }

  portals.forEach((portal) => {
    let category: string

    switch (viewType) {
      case 'value':
        // Categorize by total value (price * listings)
        if (portal.totalValue >= 60000000000) {
          // 60'000M+ CHF
          category = large
        } else if (portal.totalValue >= 10000000000) {
          // 10'000M+ CHF
          category = medium
        } else {
          category = small
        }
        break

      case 'regions':
        // Categorize by regional coverage
        if (portal.regionCount >= 26) {
          // 26+ regions
          category = large
        } else if (portal.regionCount >= 20) {
          // 20-25 regions
          category = medium
        } else {
          // 1-19 regions
          category = small
        }
        break

      default: // 'listings'
        // Categorize by listing count
        if (portal.listingCount >= 30000) {
          // 30,000+ listings
          category = large
        } else if (portal.listingCount >= 10000) {
          category = medium
        } else {
          category = small
        }
    }

    groups[category].push(portal)
  })

  return groups
}

export const getTreemapData = (
  portals: PortalData[],
  viewType: ViewType,
  t: TranslationFn = defaultT
) => {
  const groups = categorizePortals(portals, viewType, t)
  const colors = getChartColors()

  const children = []
  let colorIndex = 0

  for (const [groupName, groupPortals] of Object.entries(groups)) {
    if (groupPortals.length === 0) continue

    const groupChildren = groupPortals.map((portal) => {
      let value: number
      switch (viewType) {
        case 'value':
          value = portal.totalValue
          break
        case 'regions':
          value = portal.regionCount
          break
        default:
          value = portal.listingCount
      }

      return {
        name: portal.name,
        value,
        data: portal,
        itemStyle: {
          color: colors[colorIndex++ % colors.length],
        },
      }
    })

    children.push({
      name: groupName,
      value: groupChildren.reduce((sum, child) => sum + child.value, 0),
      children: groupChildren,
    })
  }

  return {
    name: t('portalAnalysis.treemap.swissRealEstatePortals'),
    children,
  }
}

export const getChartColors = () => [
  '#0294df', // Primary blue
  '#33c0ed', // Lighter shade
  '#66d3f3', // Even lighter
  '#99e5fa', // Very light
  '#10b981',
  '#34d399',
  '#6ee7b7',
  '#f59e0b',
  '#fbbf24',
  '#fde68a',
  '#8b5cf6',
  '#a78bfa',
  '#c4b5fd',
  '#ef4444',
  '#f87171',
  '#fca5a5',
  '#06b6d4',
  '#22d3ee',
  '#67e8f9',
  '#ec4899',
  '#f472b6',
  '#f9a8d4',
]

export const getConcentrationLevel = (top5Share: number): string => {
  if (top5Share > 70) return 'Very high concentration'
  if (top5Share > 50) return 'High concentration'
  if (top5Share > 30) return 'Moderate concentration'
  return 'Low concentration'
}

export interface Insight {
  title: string
  description: string
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: PortalAnalysisData,
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (data.portals.length === 0) return insights

  // 1. Portal market concentration
  const top5Portals = [...data.portals].sort((a, b) => b.listingCount - a.listingCount).slice(0, 5)
  const top5ListingCount = top5Portals.reduce((sum, p) => sum + p.listingCount, 0)
  insights.push({
    title: t('charts.insights.portal.portalMarketConcentration'),
    description: t('charts.insights.descriptions.portal.portalMarketConcentration', {
      percentage: data.top5Share.toFixed(0),
      count: top5ListingCount.toLocaleString(),
    }),
  })

  // 2. Portal ecosystem
  const groups = categorizePortals(data.portals, 'listings', t)
  const large = t('portalAnalysis.treemap.largePortals')
  const medium = t('portalAnalysis.treemap.mediumPortals')
  const small = t('portalAnalysis.treemap.smallPortals')
  const majorCount = (groups[large] || []).length
  const regionalCount = (groups[medium] || []).length
  const nicheCount = (groups[small] || []).length

  insights.push({
    title: t('charts.insights.portal.portalEcosystem'),
    description: t('charts.insights.descriptions.portal.portalEcosystem', {
      large: majorCount,
      threshold: '1000',
      medium: regionalCount,
      small: nicheCount,
    }),
  })

  // 3. Geographic reach
  const avgRegions = data.portals.reduce((sum, p) => sum + p.regionCount, 0) / data.portals.length
  data.portals.filter((p) => p.regionCount > 20).length

  const topReachPortal = [...data.portals].sort((a, b) => b.regionCount - a.regionCount)[0]
  insights.push({
    title: t('charts.insights.portal.geographicReach'),
    description: t('charts.insights.descriptions.portal.geographicReach', {
      avgRegions: avgRegions.toFixed(1),
      topPortal: topReachPortal?.name || '',
      topRegions: topReachPortal?.regionCount || 0,
    }),
  })

  // 4. Price positioning
  const totalListings = data.portals.reduce((sum, p) => sum + p.listingCount, 0)
  const avgPrice =
    data.portals.reduce((sum, p) => sum + p.avgPrice * p.listingCount, 0) / totalListings
  data.portals.filter((p) => p.avgPrice > avgPrice * 1.2).length

  const highestPricePortal = [...data.portals].sort((a, b) => b.avgPrice - a.avgPrice)[0]
  insights.push({
    title: t('charts.insights.portal.pricePositioning'),
    description: t('charts.insights.descriptions.portal.pricePositioning', {
      avgPrice: formatPricePerSqm(avgPrice),
      highPortal: highestPricePortal?.name || '',
      highPrice: formatPricePerSqm(highestPricePortal?.avgPrice || 0),
    }),
  })

  // 5. Market efficiency
  const top10Portals = data.portals.slice(0, 10)
  const top10Listings = top10Portals.reduce((sum, p) => sum + p.listingCount, 0)
  const marketEfficiency = (top10Listings / data.totalListings) * 100

  const efficiencyLevel =
    marketEfficiency > 80
      ? t('charts.common.high') || 'high'
      : marketEfficiency > 60
        ? t('charts.common.moderate') || 'moderate'
        : t('charts.common.low') || 'low'
  insights.push({
    title: t('charts.insights.portal.marketEfficiency'),
    description: t('charts.insights.descriptions.portal.marketEfficiency', {
      percentage: marketEfficiency.toFixed(0),
      efficiency: efficiencyLevel,
    }),
  })

  return insights
}

export const processEvolutionData = (aggregations: any) => {
  if (!aggregations?.over_time?.buckets) {
    return null
  }

  // Define the specific portals we're tracking
  const targetPortals = [
    'homegate.ch',
    'immoscout24.ch',
    'Properstar',
    'newhome.ch',
    'BuyRent',
    'iCasa',
    'UrbanHome.ch',
    'Immobilier.ch',
    'Dreamo',
    'Flatfox',
  ]

  // Process time series data
  const timeSeriesData: any = {}
  const quarters: string[] = []

  aggregations.over_time.buckets.forEach((timeBucket: any) => {
    const quarter = timeBucket.key_as_string
    quarters.push(quarter)

    const totalQuarterlyListings = timeBucket.total_quarterly_listings?.value || 0

    // Process each portal's data for this quarter
    if (timeBucket.by_portal?.portal_names?.buckets) {
      timeBucket.by_portal.portal_names.buckets.forEach((portalBucket: any) => {
        const portalName = portalBucket.key

        // Only include target portals
        if (targetPortals.includes(portalName)) {
          if (!timeSeriesData[portalName]) {
            timeSeriesData[portalName] = []
          }

          const listingCount = portalBucket.back_to_listing.listing_count?.value || 0
          const marketShare =
            totalQuarterlyListings > 0 ? (listingCount / totalQuarterlyListings) * 100 : 0

          timeSeriesData[portalName].push({
            quarter,
            listingCount,
            marketShare,
          })
        }
      })
    }

    // Fill in zeros for portals that don't have data for this quarter
    targetPortals.forEach((portal: string) => {
      if (!timeSeriesData[portal]) {
        timeSeriesData[portal] = []
      }

      const hasDataForQuarter = timeSeriesData[portal].some((d: any) => d.quarter === quarter)
      if (!hasDataForQuarter) {
        timeSeriesData[portal].push({
          quarter,
          listingCount: 0,
          marketShare: 0,
        })
      }
    })
  })

  // Sort data by quarter for each portal
  Object.keys(timeSeriesData).forEach((portal) => {
    timeSeriesData[portal].sort(
      (a: any, b: any) => new Date(a.quarter).getTime() - new Date(b.quarter).getTime()
    )
  })

  return {
    portals: targetPortals,
    quarters: quarters.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    seriesData: timeSeriesData,
  }
}
